import React, { CSSProperties } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'

import { ActiveIncidentCard } from '@hivewatch/dark'

import { NotificationSubtitle } from '@/features/notification'
import { IncidentType } from '@/graphql/generated/schemas'
import { getDetectionTypeIcon } from '@/utils/incidentIcons'

interface IncidentDataIProps {
  id: string
  name: string
  title: string
  dateTime: string
  facilityName: string
  facility: string // TODO - This is the facility short name - rename to reflect this
  type?: IncidentType | null
  isManualIncident: boolean
  slug?: string | null
  displayId: string
  handleClick: () => void
}

interface IncidentsListItemIProps {
  data: IncidentDataIProps[]
  index: number
  style?: CSSProperties
}

/**
 * A single active-incident row in the notifications drawer, rendered with the
 * dark package's ActiveIncidentCard (icon + link-coloured title + subtitle).
 * The icon comes from the shared getDetectionTypeIcon dark-icon mapping (same
 * glyphs as the incident drawer); the subtitle is the web-app
 * NotificationSubtitle passed in as a slot. ActiveIncidentCard is a plain div,
 * so this row supplies its own role/tabIndex/onKeyDown for keyboard access.
 */
export const IncidentsListItem = ({
  data,
  index,
  style,
}: IncidentsListItemIProps) => {
  const colorScheme = useLightMode()
  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )
  const {
    id,
    name,
    title,
    dateTime,
    facilityName,
    facility: facilityShortName,
    type,
    isManualIncident: isManual,
    slug: rawSlug,
    displayId,
    handleClick,
  } = data[index]
  // Slug-based icon mapping only applies under the incident-type-definition
  // flag (mirrors the incident drawer + the legacy IncidentIcon behaviour).
  const slug = isIncidentTypeDefinition ? rawSlug : null

  return (
    <ActiveIncidentCard
      colorScheme={colorScheme}
      cursor='pointer'
      data-testid={`notificationsTab_incident_${id}`}
      icon={getDetectionTypeIcon(type, slug, isManual, colorScheme, name)}
      onClick={handleClick}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleClick()
        }
      }}
      role='button'
      style={style}
      subtitle={
        <NotificationSubtitle
          facilityName={facilityName}
          facilityShortName={facilityShortName}
          fontSize='11px'
          maxW='unset'
          sourceName={name}
          timestamp={dateTime}
        />
      }
      tabIndex={0}
      title={`${displayId} ${title}`}
    />
  )
}
